import { ProductSupplierDoc } from '@gooduncles/gu-app-schema';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';
import { ColDef, ColGroupDef, RowClassParams, RowStyle } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { groupBy } from 'lodash-es';
import { FC, useMemo } from 'react';

import { PurchaseOrderPreviewForSupplier } from 'src/hooks/swr/useSWRPurchaseOrderPreview';

import { AgCellCopyValue } from 'src/atomic-components/atoms/AgCell/AgCellCopyValue';
import AgCellDownloadPurchaseOrder from 'src/atomic-components/atoms/AgCell/AgCellDownloadPurchaseOrder';

type RowData = PurchaseOrderPreviewForSupplier & { productSupplier: ProductSupplierDoc | undefined };

interface SupplierPurchaseOrderPreviewTableProps {
  rowData: RowData[];
  showHeader: boolean;
  messageWrapper: {
    messagePrefix: string;
    messageSuffix: string;
    date: string;
  };
}

const defaultColDef: ColDef<RowData> = {
  sortable: true,
  resizable: true,
  filter: true,
};

const getRowStyle = (params: RowClassParams<RowData>): RowStyle | undefined => {
  const data = params.data as RowData;
  if (!data.productSupplier?.name) {
    return { backgroundColor: 'var(--red100)' };
  }
};

const SupplierPurchaseOrderPreviewTable: FC<SupplierPurchaseOrderPreviewTableProps> = ({
  rowData,
  showHeader,
  messageWrapper,
}) => {
  const columnDefs: ColDef<RowData>[] = useMemo(() => {
    return [
      {
        field: '_id',
        hide: true,
      },
      {
        headerName: '거래처ID',
        field: 'productSupplier.supplierId',
        hide: true,
      },
      {
        headerName: '거래처',
        field: 'productSupplier.name',
        cellRenderer: 'agGroupCellRenderer',
        maxWidth: 200,
      },
      {
        headerName: '발주 품목 수(종류)',
        maxWidth: 150,
        valueGetter: (params) => {
          const parchaseOrder = params.data;
          if (parchaseOrder) {
            const productIds = groupBy(parchaseOrder.products, (product) => product.productId);
            return Object.keys(productIds).length;
          }
          return null;
        },
      },
      {
        headerName: '주문 매장 수',
        maxWidth: 120,
        valueGetter: (params) => {
          const parchaseOrder = params.data;
          if (parchaseOrder) {
            const storeIds = groupBy(parchaseOrder.products, (product) => product.storeId);
            return Object.keys(storeIds).length;
          }
          return null;
        },
      },
      {
        headerName: '발주서 내용',
        field: 'messageRawList',
        minWidth: 254,
        wrapText: true,
        autoHeight: true,
        cellStyle: { whiteSpace: 'pre-wrap', lineHeight: '1.5', padding: 8 },
        valueFormatter: (params) => {
          const purchaseOrder = params.data as RowData;
          const { messageRawList } = purchaseOrder;
          const { messagePrefix, messageSuffix } = params.context.messageWrapper;
          if (!purchaseOrder?.productSupplier?.name) return '';
          // 전산 방식은 불필요한 메시지를 제거한다.
          if (purchaseOrder.productSupplier.orderChannel === 'api') {
            return '';
          }
          const messageBody = messageRawList.sort((a, b) => a.localeCompare(b)).join('\n');
          return showHeader ? `${messagePrefix}${messageBody}${messageSuffix}` : messageBody;
        },
      },
      {
        headerName: '거래처 번호',
        maxWidth: 180,
        field: 'productSupplier.primaryContact',
      },
      {
        headerName: '발주서 복사',
        maxWidth: 164,
        valueGetter: (params): any => {
          const purchaseOrder = params.data as RowData;
          const { messagePrefix, messageSuffix } = params.context.messageWrapper;
          if (!purchaseOrder?.productSupplier) return null;
          const { messageRawList } = purchaseOrder;
          if (purchaseOrder.productSupplier.orderChannel === 'api') {
            return messageRawList;
          }
          const value = messageRawList.join('\n');
          return `${messagePrefix}${value}${messageSuffix}`;
        },
        cellRenderer: (params: any): any => {
          const purchaseOrder = params.data as RowData;
          return purchaseOrder.productSupplier?.orderChannel === 'api' ? (
            <AgCellDownloadPurchaseOrder {...params} />
          ) : (
            <AgCellCopyValue {...params} />
          );
        },
        cellRendererParams: (params: any): any => {
          const purchaseOrder = params.data as RowData;
          if (!purchaseOrder.productSupplier) return null;
          const { date } = params.context.messageWrapper;
          return {
            name:
              purchaseOrder.productSupplier.orderChannel === 'api'
                ? `${purchaseOrder.productSupplier.name}-${date}`
                : `거래처 '${purchaseOrder.productSupplier.name}' 발주서`,
          };
        },
      },
    ];
  }, [showHeader]);

  const detailCellRendererParams = useMemo(() => {
    const detailColumnDefs: (ColDef<RowData['products'][number]> | ColGroupDef<RowData['products'][number]>)[] = [
      {
        headerName: '주문매장',
        field: 'storeNickname',
      },
      {
        headerName: '상품ID',
        field: 'productId',
        maxWidth: 120,
      },
      {
        headerName: '상품명',
        field: 'fullName',
      },
      {
        headerName: '주문량',
        field: 'volume',
      },
      {
        headerName: '거래처 발주명',
        field: 'inventory.purchaseConfig.purchaseProductName',
      },
      {
        headerName: '단위 변환 값',
        field: 'inventory.purchaseConfig.purchaseMultiplyUnit',
      },
      {
        headerName: '발주 단위',
        field: 'inventory.purchaseConfig.purchaseUnit',
      },
    ];

    return {
      // 부모 행이 변경될 때 같이 변경된다.
      refreshStrategy: 'rows',
      detailGridOptions: {
        columnDefs: detailColumnDefs,
        defaultColDef,
        enableCellChangeFlash: true,
      },
      getDetailRowData: (params: any) => {
        const purchaseOrder = params.data as RowData;
        const purchaseOrderProducts = purchaseOrder.products;
        params.successCallback(purchaseOrderProducts);
      },
    };
  }, []);

  return (
    <div className='ag-theme-alpine height100'>
      <AgGridReact
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        getRowStyle={getRowStyle}
        // rowId를 지정해주지 않으면 데이터 변경시 refresh가 발생한다.
        getRowId={(params) => params.data.supplierId}
        context={{ messageWrapper }}
        // 편집 완료후 스크롤 이동을 막는다.
        suppressScrollOnNewData={true}
        // 툴팁의 노출 속도
        tooltipShowDelay={0}
        // 하위 노드 관련 설정
        masterDetail={true}
        keepDetailRows={true}
        detailRowAutoHeight={true}
        detailCellRendererParams={detailCellRendererParams}
      />
    </div>
  );
};

export default SupplierPurchaseOrderPreviewTable;
